import { memo, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "./ui/button";
import { SelectField } from "./ui/select";
import { Visualization } from "./report-visualization";
import { INTL_LOCALES, getLocale, tx, type Locale } from "@/lib/i18n";
import {
  matrixResult,
  type GeneratedReport,
  type Parameters,
  type ResolvedSection,
  type WidgetDefinition,
} from "@/lib/reporting";
import type { Cell, QueryResult, ResultField } from "@/lib/types";

const numberFormats = new Map<Locale, Intl.NumberFormat>();
const timestampFormats = new Map<Locale, Intl.DateTimeFormat>();
function cellText(
  value: Cell,
  field: ResultField,
  locale: Locale,
  matrix = false,
): string {
  if (value === null || value === undefined)
    return tx(matrix ? "Unreported" : "Not available", {}, locale);
  if (typeof value === "number") {
    if (!numberFormats.has(locale))
      numberFormats.set(
        locale,
        new Intl.NumberFormat(INTL_LOCALES[locale], {
          maximumFractionDigits: 2,
        }),
      );
    return (
      numberFormats.get(locale)!.format(value) +
      (field.format === "hours"
        ? ` ${tx("h", {}, locale)}`
        : field.format === "minutes"
          ? ` ${tx("min", {}, locale)}`
          : "")
    );
  }
  if (field.type === "timestamp") {
    if (!timestampFormats.has(locale))
      timestampFormats.set(
        locale,
        new Intl.DateTimeFormat(INTL_LOCALES[locale], {
          dateStyle: "short",
          timeStyle: "short",
          timeZone: "Europe/Helsinki",
        }),
      );
    return timestampFormats.get(locale)!.format(new Date(String(value)));
  }
  if (field.type === "date") return String(value).slice(0, 10);
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}
function DocumentTable({
  data,
  locale,
  matrix = false,
  limit,
  context,
}: {
  data: QueryResult;
  locale: Locale;
  matrix?: boolean;
  limit?: number;
  context?: string;
}) {
  const rows = limit ? data.rows.slice(0, limit) : data.rows;
  return (
    <div className="report-table-scroll">
      <table className={`document-table ${matrix ? "matrix-table" : ""}`}>
        <thead>
          {context && (
            <tr className="print-context">
              <th colSpan={data.fields.length}>{context}</th>
            </tr>
          )}
          <tr>
            {data.fields.map((f) => (
              <th key={f.name}>{f.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {data.fields.map((f) => (
                <td
                  key={f.name}
                  className={
                    row[f.name] === null && matrix
                      ? "unreported-cell"
                      : undefined
                  }
                >
                  {cellText(row[f.name], f, locale, matrix)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!data.rows.length && (
        <p className="p-4 text-sm">{tx("No records", {}, locale)}</p>
      )}
    </div>
  );
}
export function csvForResult(data: QueryResult): string {
  const escape = (value: unknown) => {
    const text =
      value == null
        ? ""
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
    // Keep text cells literal when the CSV is opened by a spreadsheet application.
    const literal =
      typeof value === "string" && /^[=+@\-]/.test(text) ? `'${text}` : text;
    return `"${literal.replaceAll('"', '""')}"`;
  };
  return [
    data.fields.map((f) => escape(f.label)),
    ...data.rows.map((row) => data.fields.map((f) => escape(row[f.name]))),
  ]
    .map((row) => row.join(","))
    .join("\r\n");
}
export function downloadResult(data: QueryResult, name: string) {
  const url = URL.createObjectURL(
    new Blob(["\ufeff", csvForResult(data)], {
      type: "text/csv;charset=utf-8",
    }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/[^\p{L}\p{N} _-]/gu, "-")}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function completeMatrix(
  data: QueryResult,
  from: string,
  to: string,
): QueryResult {
  const weeks = matrixResult(data, from, to);
  if (!weeks.length) throw new Error("Select a valid billing period.");
  return {
    ...data,
    fields: [
      ...weeks[0].fields,
      ...weeks.slice(1).flatMap((w) => w.fields.slice(3)),
    ],
    columns: [
      ...weeks[0].columns,
      ...weeks.slice(1).flatMap((w) => w.columns.slice(3)),
    ],
    rows: data.rows.map((_, i) =>
      Object.assign({}, ...weeks.map((w) => w.rows[i])),
    ),
  };
}
function MatrixView({
  data,
  parameters,
  locale,
  documentMode,
}: {
  data: QueryResult;
  parameters: Parameters;
  locale: Locale;
  documentMode?: boolean;
}) {
  const from = String(parameters.from ?? "2026-10-12"),
    to = String(parameters.to ?? "2026-11-15");
  const weeks = useMemo(() => matrixResult(data, from, to), [data, from, to]),
    [index, setIndex] = useState(0),
    [group, setGroup] = useState("all");
  const selected = weeks[Math.min(index, weeks.length - 1)];
  const localize = (result: QueryResult): QueryResult => ({
    ...result,
    fields: result.fields.map((f) => ({
      ...f,
      label: f.label.includes(" · ")
        ? f.label.split(" · ")[0] +
          " · " +
          tx(f.label.split(" · ")[1], {}, locale)
        : tx(f.label, {}, locale),
    })),
  });
  if (!selected) return null;
  return (
    <div>
      <div className="no-print mb-3 flex flex-wrap gap-2">
        <SelectField
          aria-label={tx("Week")}
          value={String(Math.min(index, weeks.length - 1))}
          onValueChange={(v) => setIndex(Number(v))}
          className="w-auto"
        >
          {weeks.map((w, i) => (
            <option key={i} value={String(i)}>
              {w.columns[3]?.slice(0, 10)} – {w.columns.at(-1)?.slice(0, 10)}
            </option>
          ))}
        </SelectField>
        <SelectField
          aria-label={tx("Reporting group")}
          value={group}
          onValueChange={setGroup}
          className="w-auto"
        >
          <option value="all">{tx("All reporting groups")}</option>
          {[...new Set(data.rows.map((r) => String(r.group_name)))].map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </SelectField>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            downloadResult(
              localize(completeMatrix(data, from, to)),
              "billing-matrix",
            )
          }
        >
          <Download />
          {tx("Export complete CSV")}
        </Button>
      </div>
      <p className="no-print mb-2 text-xs text-muted-foreground">
        {tx(
          "Unreported cells are blank in CSV. Week 42 is treated as submitted; later weeks are unreported.",
        )}
      </p>
      <div className="no-print">
        <DocumentTable
          data={{
            ...localize(selected),
            rows: selected.rows.filter(
              (r) => group === "all" || r.group_name === group,
            ),
          }}
          locale={locale}
          matrix
        />
      </div>
      {documentMode && (
        <div className="print-only">
          {weeks.map((week, i) => (
            <section key={i} className="printed-week">
              <h3>
                {tx("Property Unit service matrix", {}, locale)} ·{" "}
                {week.columns[3]?.slice(0, 10)} –{" "}
                {week.columns.at(-1)?.slice(0, 10)}
              </h3>
              <DocumentTable
                data={localize(week)}
                locale={locale}
                matrix
                context={`HCR Katinkulta · ${week.columns[3]?.slice(0, 10)} – ${week.columns.at(-1)?.slice(0, 10)}`}
              />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
export function ReportingWidget({
  widget,
  data,
  parameters = {},
  documentMode = false,
  locale = getLocale(),
  documentContext,
}: {
  widget: WidgetDefinition;
  data: QueryResult;
  parameters?: Parameters;
  documentMode?: boolean;
  locale?: Locale;
  documentContext?: string;
}) {
  if (!documentMode && widget.localizedInputs?.length)
    data = {
      ...data,
      rows: data.rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            key,
            widget.localizedInputs!.includes(key) && typeof value === "string"
              ? tx(value, {}, locale)
              : value,
          ]),
        ),
      ),
    };
  if (widget.renderer === "billing-matrix")
    return (
      <MatrixView
        data={data}
        parameters={parameters}
        locale={locale}
        documentMode={documentMode}
      />
    );
  if (!documentMode)
    return <Visualization data={data} config={widget.visualization} />;
  if (widget.visualization.type === "table")
    return (
      <DocumentTable data={data} locale={locale} context={documentContext} />
    );
  if (widget.visualization.type === "kpi") {
    const field = data.fields.find((f) => f.name === widget.visualization.y[0]);
    return (
      <p className="text-4xl font-semibold tabular-nums">
        {field
          ? cellText(data.rows[0]?.[field.name] ?? null, field, locale)
          : "—"}
      </p>
    );
  }
  return <DocumentTable data={data} locale={locale} />;
}
function Section({
  section,
  report,
}: {
  section: ResolvedSection;
  report: GeneratedReport;
}) {
  return (
    <section className="resolved-section border-t border-border pt-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">{section.title}</h2>
        {section.data && section.widget?.renderer !== "billing-matrix" && (
          <Button
            className="no-print"
            variant="ghost"
            size="sm"
            onClick={() =>
              downloadResult(section.data!, `${report.name}-${section.title}`)
            }
          >
            <Download />
            {tx("CSV")}
          </Button>
        )}
      </div>
      {section.text !== undefined && (
        <p
          className={`whitespace-pre-wrap text-sm leading-6 ${section.columns === 2 ? "sm:columns-2 print:columns-2" : ""}`}
        >
          {section.text}
        </p>
      )}
      {section.widget && section.data && (
        <ReportingWidget
          widget={section.widget}
          data={section.data}
          parameters={report.parameters}
          documentMode
          locale={report.locale}
          documentContext={`${report.name} · ${report.propertyName ?? "Luuta"} · ${Object.values(report.parameters).join(" · ")}`}
        />
      )}
    </section>
  );
}
export const ReportDocument = memo(function ReportDocument({
  report,
}: {
  report: GeneratedReport;
}) {
  return (
    <article
      className="report-document space-y-6 rounded-xl border border-border bg-card p-6"
      lang={report.locale}
    >
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
          {report.propertyName ?? "Luuta"}
        </p>
        <h1 className="mt-2 text-2xl font-semibold">{report.name}</h1>
        <p className="mt-3 text-xs text-muted-foreground">
          {tx("Generated", {}, report.locale)}{" "}
          {new Intl.DateTimeFormat(INTL_LOCALES[report.locale], {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "Europe/Helsinki",
          }).format(new Date(report.generatedAt))}{" "}
          · {tx("Source as of", {}, report.locale)} {report.sourceAt} ·{" "}
          {tx("Revision", {}, report.locale)} {report.templateRevision}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{report.id}</p>
      </header>
      {report.sections.map((section) => (
        <Section key={section.id} section={section} report={report} />
      ))}
    </article>
  );
});
